import {
  BasicButton,
  ButtonGroup,
  DateSelectField,
  Divider,
  Grid,
  GridItem,
  LegacyModal,
  Link,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTrigger,
  PhoneNumberInputField,
  PrimaryButton,
  SelectInputField,
  Text,
  TextInputField,
  Toggle,
} from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { Field, Form, Formik } from "formik";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Item } from "react-stately";
import { date, lazy, object, string } from "yup";

import { FormikErrorFocus } from "../../../../components/FormikErrorFocus";
import { useUser } from "../../../../hooks/api/useUser";

type EmployerReferenceModalProps = {
  index: number;
  reference?: components["schemas"]["EmploymentReferencesValue"];
  onSave: (
    reference: components["schemas"]["EmploymentReferencesValue"],
    index: number
  ) => void;
  onDelete?: () => void;
  trigger: JSX.Element;
  requirePhone: boolean;
  requireEmail: boolean;
  defaultOpen?: boolean;
};

export function EmployerReferenceModal({
  reference,
  onSave,
  onDelete,
  index,
  trigger,
  requireEmail,
  requirePhone,
  defaultOpen = false,
}: EmployerReferenceModalProps) {
  const { t } = useTranslation();
  const { user } = useUser();

  const [isCurrentReference, setIsCurrentReference] = useState<boolean>(
    !!reference && !reference.end_date
  );

  const schema = object({
    company_name: string().required().label(t("Company")),
    position: string().trim().required().label(t("Position")),
    first_name: string().trim().required().label(t("First name")),
    last_name: string().trim().required().label(t("Last name")),
    reference_job_title: string().trim().required().label(t("Job title")),
    email: lazy(() => {
      if (requireEmail) {
        return string()
          .trim()
          .email(t("Please provide a valid email address."))
          .required(t("Please provide a valid email address."))
          .test(
            "own-email",
            t("Unable to use own email address"),
            (value) => value !== user?.email
          );
      }
      return string()
        .trim()
        .email(t("Please provide a valid email address."))
        .notRequired()
        .test(
          "own-email",
          t("Unable to use own email address"),
          (value) => value !== user?.email
        );
    }),
    phone_number: lazy(() => {
      if (requirePhone) {
        return string()
          .trim()
          .nonNullable()
          .required(t("Please enter a valid phone number."))
          .test(
            "own-phone-number",
            t("Unable to use own phone number"),
            (value) =>
              value !== user?.personal_profile?.profile_phone_numbers[0].number
          );
      }

      return string()
        .trim()
        .optional()
        .nonNullable()
        .required(t("Please enter a valid phone number."))
        .test(
          "own-phone-number",
          t("Unable to use own phone number"),
          (value) =>
            value !== user?.personal_profile?.profile_phone_numbers[0].number
        );
    }),
    relationship: string().trim().required().label(t("Reference relationship")),
    start_date: date().required().label(t("Start date")),
    end_date: date().when(["start_date"], ([start_date], schema) => {
      if (isCurrentReference) {
        return schema.notRequired();
      }

      if (start_date) {
        return schema
          .required()
          .label(t("End date"))
          .min(start_date, t("End date must be after start date"));
      }

      return schema.required().label(t("End date"));
    }),
  });

  const relationshipOptions = [
    {
      key: "You reported to them",
      displayValue: t("You reported to them"),
    },
    {
      key: "They were your senior but did not manage you directly",
      displayValue: t("They were your senior but did not manage you directly"),
    },
    {
      key: "You worked with them",
      displayValue: t("You worked with them"),
    },
    {
      key: "You managed them",
      displayValue: t("You managed them"),
    },
    {
      key: "Other",
      displayValue: t("Other"),
    },
  ];

  return (
    <ModalTrigger defaultOpen={defaultOpen} trigger={() => trigger}>
      {(close) => (
        <Formik
          initialValues={
            reference ?? {
              company_name: "",
              position: "",
              start_date: null,
              end_date: null,
              first_name: "",
              last_name: "",
              reference_job_title: "",
              email: "",
              phone_number: "",
              relationship: null,
              index,
            }
          }
          onSubmit={(reference) => {
            onSave(
              reference as components["schemas"]["EmploymentReferencesValue"],
              index
            );
            close();
          }}
          validationSchema={schema}
        >
          {({ values, handleSubmit, setFieldValue }) => {
            return (
              <LegacyModal width="456px">
                <ModalHeader
                  close={close}
                  title={
                    reference
                      ? t("Edit employment reference")
                      : t("Add employment reference")
                  }
                  testID="employment-reference-modal"
                />
                <ModalContent>
                  <Form>
                    <Grid form>
                      <GridItem>
                        <Text style="summary/content-primary">
                          <Trans>My employment details</Trans>
                        </Text>
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired
                          name="company_name"
                          label={t("Company")}
                          as={TextInputField}
                          testID="employment-reference-modal-company-name-input"
                        />
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired
                          name="position"
                          label={t("Position")}
                          as={TextInputField}
                          testID="employment-reference-modal-position-input"
                        />
                      </GridItem>

                      <GridItem>
                        <Field
                          label={t("Start date")}
                          name="start_date"
                          isApproximateDate
                          isRequired
                          value={values.start_date}
                          as={DateSelectField}
                          testID="employment-reference-modal-start-date-select"
                        />
                      </GridItem>

                      <GridItem
                        alignItems="left"
                        className={css({
                          flexDirection: "row",
                        })}
                      >
                        <Toggle
                          isSelected={isCurrentReference}
                          onChange={(value) => {
                            setFieldValue("end_date", null);
                            setIsCurrentReference(value);
                          }}
                          testID="employment-reference-modal-currently-working"
                        >
                          {t("I am currently working at this company")}
                        </Toggle>
                      </GridItem>

                      {!isCurrentReference && (
                        <GridItem>
                          <Field
                            label={t("End date")}
                            name="end_date"
                            isApproximateDate
                            isRequired
                            value={values.end_date}
                            as={DateSelectField}
                            testID="employment-reference-modal-end-date-select"
                          />
                        </GridItem>
                      )}
                      <GridItem>
                        <Divider />
                      </GridItem>

                      <GridItem>
                        <Text style="summary/content-primary">
                          <Trans>Reference contact details</Trans>
                        </Text>
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired
                          name="first_name"
                          label={t("First name")}
                          as={TextInputField}
                          testID="employment-reference-modal-reference-first-name-input"
                        />
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired
                          name="last_name"
                          label={t("Last name")}
                          as={TextInputField}
                          testID="employment-reference-modal-reference-last-name-input"
                        />
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired
                          name="reference_job_title"
                          label={t("Job title")}
                          as={TextInputField}
                          testID="employment-reference-modal-reference-job-title-input"
                        />
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired={requireEmail}
                          name="email"
                          label={t("Email")}
                          as={TextInputField}
                          testID="employment-reference-modal-reference-email-input"
                        />
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired={requirePhone}
                          type="string"
                          as={PhoneNumberInputField}
                          label={t("Phone number")}
                          placeholder={t("Phone number")}
                          name="phone_number"
                          testID="employment-reference-modal-reference-phone-number"
                        />
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired
                          as={SelectInputField}
                          label={t("Reference relationship")}
                          placeholder={t("Please select")}
                          name="relationship"
                          testID="employment-reference-modal-reference-relationship-select"
                        >
                          {relationshipOptions?.map((relationship) => (
                            <Item key={relationship.key}>
                              {relationship.displayValue}
                            </Item>
                          ))}
                        </Field>
                      </GridItem>
                    </Grid>

                    <FormikErrorFocus />
                  </Form>
                </ModalContent>
                <ModalFooter
                  className={css({
                    justifyContent: onDelete ? "space-between" : "flex-end",
                  })}
                >
                  {onDelete && (
                    <Link
                      textStyle="global/body-secondary"
                      textColor="text-weak"
                      onPress={() => {
                        onDelete();
                        close();
                      }}
                      testID="employer-reference-modal-delete-reference-button"
                    >
                      <Trans>Delete reference</Trans>
                    </Link>
                  )}
                  <ButtonGroup>
                    <BasicButton
                      size="medium"
                      onPress={close}
                      testID="employer-reference-modal-cancel-button"
                    >
                      <Trans>Cancel</Trans>
                    </BasicButton>
                    <PrimaryButton
                      type="submit"
                      size="medium"
                      onPress={() => handleSubmit()}
                      testID="employer-reference-modal-save-button"
                    >
                      <Trans>Save employer</Trans>
                    </PrimaryButton>
                  </ButtonGroup>
                </ModalFooter>
              </LegacyModal>
            );
          }}
        </Formik>
      )}
    </ModalTrigger>
  );
}
